/**
 * useEmailProviders — 邮箱 OAuth + 邮件管理钩子
 *
 * Architecture 3.0 Phase 4: 从 AppLayout.tsx 抽离
 * 封装邮箱的连接/断开/邮件加载/翻页/阅读逻辑
 */

import { useState, useCallback } from 'react';
import { API_BASE_URL } from '../services/api';

// ─── Types ─────────────────────────────────

export interface EmailProvider {
  source: string;
  displayName: string;
  connected: boolean;
}

export interface EmailEntry {
  id: string;
  subject: string;
  from: string;
  date: string;
  preview: string;
  unread: boolean;
}

export interface EmailMessagesState {
  source: string;
  emails: EmailEntry[];
  error?: string;
  hasMore?: boolean;
  nextPageToken?: string;
  skip?: number;
  loadingMore?: boolean;
}

export interface ReadingEmailState {
  source: string;
  id: string;
  subject: string;
  content?: string;
  bodyHtml?: string;
  from?: string;
  date?: string;
  isLoading?: boolean;
  error?: string;
}

// ─── 邮件数据解析辅助 ─────────────────────

function parseEmailEntry(e: any): EmailEntry {
  return {
    id: e.id,
    subject: e.subject || '(no subject)',
    from: e.from?.name ? `${e.from.name}` : e.from?.address || '',
    date: e.receivedAt,
    preview: e.preview || '',
    unread: !e.isRead,
  };
}

// ─── Hook ──────────────────────────────────

export function useEmailProviders() {
  const [emailProviders, setEmailProviders] = useState<EmailProvider[]>([]);
  const [emailMessages, setEmailMessages] = useState<EmailMessagesState[]>([]);
  const [readingEmail, setReadingEmail] = useState<ReadingEmailState | null>(null);

  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  }), []);

  // ─── 加载邮件列表 ──────────────────────────
  const loadEmailMessages = useCallback(async (source: string) => {
    try {
      const resp = await fetch(`${API_BASE_URL}/api/email/${source}/messages?top=50`, {
        headers: getAuthHeaders(),
      });
      const data = await resp.json();
      if (!resp.ok || data.error) {
        const errMsg = data.error || `HTTP ${resp.status}`;
        console.error(`[Email] ${source} messages failed:`, errMsg);
        setEmailMessages(prev => {
          const filtered = prev.filter(em => em.source !== source);
          return [...filtered, { source, emails: [], error: errMsg }];
        });
        return;
      }
      setEmailMessages(prev => {
        const filtered = prev.filter(em => em.source !== source);
        return [...filtered, {
          source,
          emails: (data.emails || []).map(parseEmailEntry),
          hasMore: data.hasMore || false,
          nextPageToken: data.nextPageToken || undefined,
          skip: (data.emails || []).length,
        }];
      });
    } catch (err: any) {
      console.error(`[Email] ${source} loadMessages exception:`, err);
      setEmailMessages(prev => {
        const filtered = prev.filter(em => em.source !== source);
        return [...filtered, { source, emails: [], error: err?.message || 'Network error' }];
      });
    }
  }, [getAuthHeaders]);

  // ─── OAuth 连接 ────────────────────────────
  const handleEmailConnect = useCallback(async (source: string) => {
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await fetch(`${API_BASE_URL}/api/email/${source}/auth`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Origin': window.location.origin },
      });
      const data = await resp.json();
      if (data.authUrl) {
        const popup = window.open(data.authUrl, `connect-${source}`, 'width=600,height=700,popup=yes');
        const listener = async (event: MessageEvent) => {
          if (event.data?.type === 'cloud-oauth-callback' && event.data?.source === source) {
            window.removeEventListener('message', listener);
            popup?.close();
            const cbResp = await fetch(`${API_BASE_URL}/api/email/${source}/callback`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ code: event.data.code, redirectUri: `${window.location.origin}/app/cloud-callback` }),
            });
            if (cbResp.ok) {
              setEmailProviders(prev => prev.map(p => p.source === source ? { ...p, connected: true } : p));
              loadEmailMessages(source);
            }
          }
        };
        window.addEventListener('message', listener);
      }
    } catch (err) {
      console.error('[Email] Connect failed:', err);
    }
  }, [loadEmailMessages]);

  // ─── 断开连接 ──────────────────────────────
  const handleEmailDisconnect = useCallback(async (source: string) => {
    const token = localStorage.getItem('auth_token');
    await fetch(`${API_BASE_URL}/api/email/${source}/disconnect`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    setEmailProviders(prev => prev.map(p => p.source === source ? { ...p, connected: false } : p));
    setEmailMessages(prev => prev.filter(em => em.source !== source));
  }, []);

  // ─── 加载更多邮件 (翻页) ──────────────────
  const loadMoreEmails = useCallback(async (source: string) => {
    const entry = emailMessages.find(em => em.source === source);
    if (!entry || !entry.hasMore || entry.loadingMore) return;

    setEmailMessages(prev => prev.map(em => em.source === source ? { ...em, loadingMore: true } : em));
    try {
      const token = localStorage.getItem('auth_token');
      const params = new URLSearchParams({ top: '50' });
      if (entry.nextPageToken) params.set('pageToken', entry.nextPageToken);
      else if (entry.skip) params.set('skip', String(entry.skip));
      const resp = await fetch(`${API_BASE_URL}/api/email/${source}/messages?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await resp.json();
      if (!resp.ok || data.error) return;
      const newEmails = (data.emails || []).map(parseEmailEntry);
      setEmailMessages(prev => prev.map(em => {
        if (em.source !== source) return em;
        const existingIds = new Set(em.emails.map(e => e.id));
        const uniqueNew = newEmails.filter((e: any) => !existingIds.has(e.id));
        return {
          ...em,
          emails: [...em.emails, ...uniqueNew],
          hasMore: data.hasMore || false,
          nextPageToken: data.nextPageToken || undefined,
          skip: (em.skip || 0) + newEmails.length,
        };
      }));
    } catch (err: any) {
      console.error(`[Email] ${source} loadMore exception:`, err);
    } finally {
      setEmailMessages(prev => prev.map(em => em.source === source ? { ...em, loadingMore: false } : em));
    }
  }, [emailMessages]);

  // ─── 阅读单封邮件 ──────────────────────────
  const handleEmailRightClick = useCallback(async (source: string, id: string, subject: string) => {
    setReadingEmail({ source, id, subject, isLoading: true });
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await fetch(`${API_BASE_URL}/api/email/${source}/message/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await resp.json();
      if (resp.ok && data.email) {
        const fromStr = data.email.from?.name
          ? `${data.email.from.name} <${data.email.from.address}>`
          : data.email.from?.address || '';
        setReadingEmail({
          source, id, subject,
          content: data.email.body || data.email.preview || 'No content available.',
          bodyHtml: data.email.bodyHtml || undefined,
          from: fromStr,
          date: data.email.receivedAt,
          isLoading: false,
        });
      } else {
        setReadingEmail({ source, id, subject, error: data.error || 'Failed to load email', isLoading: false });
      }
    } catch (err: any) {
      setReadingEmail({ source, id, subject, error: err.message || 'Network error', isLoading: false });
    }
  }, []);

  // ─── 初始化加载 providers ─────────────────
  const initEmailProviders = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE_URL}/api/email/providers`, {
        headers: getAuthHeaders(),
      });
      const data = await resp.json();
      if (data.providers) {
        setEmailProviders(data.providers);
        data.providers.filter((p: any) => p.connected).forEach((p: any) => {
          fetch(`${API_BASE_URL}/api/email/${p.source}/messages?top=20`, { headers: getAuthHeaders() })
            .then(r => r.json())
            .then(d => {
              if (d.error) {
                console.error(`[Email] ${p.source} auto-load error:`, d.error);
                setEmailMessages(prev => [...prev.filter(em => em.source !== p.source), {
                  source: p.source,
                  emails: [],
                  error: d.error,
                }]);
              } else if (d.emails) {
                setEmailMessages(prev => [...prev.filter(em => em.source !== p.source), {
                  source: p.source,
                  emails: d.emails.map(parseEmailEntry),
                  hasMore: d.hasMore || false,
                  nextPageToken: d.nextPageToken || undefined,
                  skip: (d.emails || []).length,
                }]);
              }
            }).catch((err) => {
              console.error(`[Email] ${p.source} auto-load exception:`, err);
              setEmailMessages(prev => [...prev.filter(em => em.source !== p.source), {
                source: p.source,
                emails: [],
                error: 'Network error',
              }]);
            });
        });
      }
    } catch {
      // silent fail
    }
  }, [getAuthHeaders]);

  return {
    emailProviders,
    setEmailProviders,
    emailMessages,
    readingEmail,
    setReadingEmail,
    handleEmailConnect,
    handleEmailDisconnect,
    loadEmailMessages,
    loadMoreEmails,
    handleEmailRightClick,
    initEmailProviders,
  };
}
